import React from 'react';
import {
  FiCheckCircle,
  FiClock,
  FiCornerDownRight,
  FiEye,
  FiLoader,
  FiRotateCcw,
  FiZap,
} from 'react-icons/fi';
import type { AgentVersionResponse } from '@troia-v3/types';

import { useSnapshot, useVersionMetrics } from '../../hooks/useAgentTraining';

interface VersionRowProps {
  agentId: string;
  version: AgentVersionResponse;
  /** Versão atualmente ativa do agente (a mais recente) */
  isActive: boolean;
  /**
   * ID da versão imediatamente anterior na lista. Usado como fallback
   * quando a versão atual não tem snapshots próprios — mostra o score
   * herdado do treino anterior com aviso "Baseado no treino anterior".
   */
  previousVersionId: string | undefined;
  /** Label da versão anterior pra mostrar no aviso (ex: "v1.0.0") */
  previousVersionLabel: string | undefined;
  /** Cliente clicou em "Comparar" — abre PromptDiffModal */
  onCompare: (version: AgentVersionResponse) => void;
  /** Cliente clicou em "Restaurar" — confirma + cria nova versão */
  onRestore: (version: AgentVersionResponse) => void;
  /** Mutação de restore em andamento */
  restoring: boolean;
}

const CHANGE_TYPE_BADGE: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  major: { label: 'Major', bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' },
  minor: { label: 'Minor', bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308' },
  patch: { label: 'Patch', bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' },
  initial: { label: 'Inicial', bg: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' },
};

/**
 * Linha de uma versão na tab "Versões". Combina dados estáticos da versão
 * (vindos de `/ai-agents/:id/versions`) com métricas agregadas dos
 * snapshots vinculados (vindos de `/agent-training/:agentId/versions/:versionId/metrics`).
 *
 * Cada linha tem sua própria query de métricas (lazy loaded por hook), o
 * que é OK porque versões são poucas (default limit 50).
 */
export const VersionRow: React.FC<VersionRowProps> = ({
  agentId,
  version,
  isActive,
  previousVersionId,
  previousVersionLabel,
  onCompare,
  onRestore,
  restoring,
}) => {
  const { data: metrics, isLoading: loadingMetrics } = useVersionMetrics(
    agentId,
    version.id,
  );

  const hasOwnTraining =
    metrics !== undefined && metrics.snapshotsCount > 0;

  // ─── Priority 1: baseSnapshotId explícito na versão ───
  // Quando a versão foi criada via AgentTrainModal (Sprint UI-4), o
  // backend gravou o ID do snapshot que originou as mudanças. Buscamos
  // ele diretamente — é o vínculo mais preciso possível.
  const baseSnapshotId = version.baseSnapshotId as string | undefined;
  const shouldFetchBaseSnapshot =
    !hasOwnTraining && !!baseSnapshotId && metrics !== undefined;
  const { data: baseSnapshot } = useSnapshot(
    shouldFetchBaseSnapshot ? baseSnapshotId : undefined,
  );

  // ─── Priority 2 (fallback legado): versão imediatamente anterior ───
  // Pra versões antigas criadas antes do campo baseSnapshotId existir,
  // caímos no fallback de mostrar o treino da versão anterior como
  // referência. Menos preciso mas melhor que "Sem treino".
  const shouldFetchPreviousVersion =
    !hasOwnTraining &&
    !baseSnapshotId &&
    !!previousVersionId &&
    metrics !== undefined;
  const { data: previousMetrics } = useVersionMetrics(
    agentId,
    shouldFetchPreviousVersion ? previousVersionId : undefined,
  );

  // Determina qual fonte de score inherited usar (se houver)
  const inheritedFromBaseSnapshot =
    !hasOwnTraining &&
    !!baseSnapshot &&
    baseSnapshot.status === 'completed' &&
    baseSnapshot.summary !== null;
  const inheritedFromPreviousVersion =
    !hasOwnTraining &&
    !inheritedFromBaseSnapshot &&
    !!previousMetrics?.latestSnapshot;
  const isInherited = inheritedFromBaseSnapshot || inheritedFromPreviousVersion;

  // Métricas exibidas podem ser próprias ou herdadas
  const displayMetrics = hasOwnTraining
    ? metrics
    : inheritedFromBaseSnapshot && baseSnapshot?.summary
      ? {
          // Sintetiza um "version metrics" a partir do snapshot direto
          versionId: version.id,
          agentId,
          snapshotsCount: 1,
          completedSnapshotsCount: 1,
          latestSnapshot: baseSnapshot,
          averageOverallScore: baseSnapshot.summary.overallScore,
          averages: baseSnapshot.summary.averages,
        }
      : inheritedFromPreviousVersion
        ? previousMetrics
        : metrics;

  const badge = CHANGE_TYPE_BADGE[version.changeType] ?? {
    label: version.changeType,
    bg: 'rgba(107, 114, 128, 0.1)',
    color: '#6b7280',
  };

  const overallScorePercent = displayMetrics?.averageOverallScore
    ? Math.round(displayMetrics.averageOverallScore * 100)
    : null;
  const scoreColor =
    overallScorePercent === null
      ? '#94a3b8'
      : overallScorePercent >= 80
        ? '#10b981'
        : overallScorePercent >= 50
          ? '#f59e0b'
          : '#ef4444';

  return (
    <div
      className="rounded-lg border p-4 transition-colors"
      style={{
        backgroundColor: isActive
          ? 'rgba(139, 92, 246, 0.04)'
          : 'var(--theme-card-background)',
        borderColor: isActive
          ? 'rgba(139, 92, 246, 0.3)'
          : 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Identidade da versão */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className="flex-shrink-0 p-2 rounded-md"
            style={{
              backgroundColor: isActive
                ? 'rgba(139, 92, 246, 0.12)'
                : 'rgba(107, 114, 128, 0.08)',
              color: isActive ? '#8b5cf6' : '#6b7280',
            }}
          >
            <FiClock className="h-5 w-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                v{version.version}
              </h3>
              <span
                className="text-xs px-1.5 py-0.5 rounded font-medium"
                style={{ backgroundColor: badge.bg, color: badge.color }}
              >
                {badge.label}
              </span>
              {isActive && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                  style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
                >
                  <FiCheckCircle className="h-3 w-3" />
                  Ativa
                </span>
              )}
            </div>
            <p
              className="text-xs"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {formatDateTime(version.createdAt)}
              {version.changePercentage > 0 &&
                ` · ${Math.round(version.changePercentage)}% alterado`}
            </p>
          </div>
        </div>

        {/* Score badge */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div
              className="text-xs uppercase font-semibold tracking-wide flex items-center justify-end gap-1"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {isInherited && (
                <FiCornerDownRight className="h-3 w-3" title="Herdado" />
              )}
              Score
            </div>
            {loadingMetrics ? (
              <div
                className="text-sm flex items-center gap-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                <FiLoader className="h-3 w-3 animate-spin" />
                ...
              </div>
            ) : overallScorePercent !== null ? (
              <div
                className="text-xl font-bold leading-tight"
                style={{
                  color: scoreColor,
                  opacity: isInherited ? 0.7 : 1,
                }}
                title={
                  isInherited && previousVersionLabel
                    ? `Score herdado do treino da versão ${previousVersionLabel}`
                    : undefined
                }
              >
                {overallScorePercent}%
              </div>
            ) : (
              <div
                className="text-xs"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Sem treino
              </div>
            )}
            {hasOwnTraining && metrics && (
              <div
                className="text-xs mt-0.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {metrics.completedSnapshotsCount}/{metrics.snapshotsCount}{' '}
                {metrics.snapshotsCount === 1 ? 'treino' : 'treinos'}
              </div>
            )}
            {isInherited && (
              <div
                className="text-xs mt-0.5 italic"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {inheritedFromBaseSnapshot
                  ? 'do treino base'
                  : previousVersionLabel
                    ? `de ${previousVersionLabel}`
                    : 'herdado'}
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => onCompare(version)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-muted-text)',
              }}
              title="Comparar com versão atual"
            >
              <FiEye className="h-3 w-3" />
              Comparar
            </button>
            {!isActive && (
              <button
                type="button"
                onClick={() => onRestore(version)}
                disabled={restoring}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors"
                style={{
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                  cursor: restoring ? 'wait' : 'pointer',
                  opacity: restoring ? 0.6 : 1,
                }}
                title="Cria uma nova versão com este prompt"
              >
                {restoring ? (
                  <FiLoader className="h-3 w-3 animate-spin" />
                ) : (
                  <FiRotateCcw className="h-3 w-3" />
                )}
                Restaurar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Banner explicativo quando o score é herdado */}
      {isInherited && (
        <div
          className="mt-3 rounded-md px-3 py-2 text-xs flex items-start gap-2"
          style={{
            backgroundColor: 'rgba(99, 102, 241, 0.05)',
            color: '#4338ca',
            border: '1px solid rgba(99, 102, 241, 0.15)',
          }}
        >
          <FiCornerDownRight className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <div>
            {inheritedFromBaseSnapshot ? (
              <>
                Esta versão foi criada aplicando sugestões de um treino
                anterior. O score acima é do treino <strong>que originou</strong>{' '}
                esta versão, não do prompt atual. Treine esta versão para
                validar se as mudanças realmente melhoraram a qualidade.
              </>
            ) : (
              <>
                Esta versão ainda não foi avaliada por treinamento. O score
                acima é herdado da versão anterior{' '}
                {previousVersionLabel && (
                  <>
                    (<strong>{previousVersionLabel}</strong>){' '}
                  </>
                )}
                apenas como referência. Treine esta versão para ver o score
                real.
              </>
            )}
          </div>
        </div>
      )}

      {/* Breakdown opcional de evaluators (se houver dados — próprios ou herdados) */}
      {displayMetrics &&
        displayMetrics.averages &&
        displayMetrics.completedSnapshotsCount > 0 && (
          <div
            className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-2"
            style={{
              borderColor: 'var(--theme-card-border)',
              opacity: isInherited ? 0.7 : 1,
            }}
          >
            <Metric
              label="Relevância"
              value={displayMetrics.averages.relevance}
            />
            <Metric label="Fatos" value={displayMetrics.averages.hallucination} />
            <Metric label="Tom" value={displayMetrics.averages.tone} />
            <Metric
              label="Ferramentas"
              value={displayMetrics.averages.toolUsage}
            />
          </div>
        )}

      {/* Hint quando nunca treinou E não há fallback da versão anterior */}
      {metrics &&
        metrics.snapshotsCount === 0 &&
        !isInherited &&
        !loadingMetrics && (
          <div
            className="mt-3 pt-3 border-t text-xs flex items-center gap-1"
            style={{
              borderColor: 'var(--theme-card-border)',
              color: 'var(--theme-muted-text)',
            }}
          >
            <FiZap className="h-3 w-3" />
            Esta versão nunca foi avaliada por treinamento.
          </div>
        )}
    </div>
  );
};

const Metric: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div>
      <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        {label}
      </div>
      <div className="text-sm font-semibold" style={{ color }}>
        {pct}%
      </div>
    </div>
  );
};

function formatDateTime(iso: string | Date): string {
  try {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}
